import { isResultSuccess, } from '../types';
/**
 * DemandesApprenants service
 */
class DemandesApprenantsService {
    demandesApprenantsAdapter;
    demandesApprenantsFormatter;
    storeService;
    contributionsInformationService;
    constructor({ demandesApprenantsAdapter, demandesApprenantsFormatter, storeService, contributionsInformationService, }) {
        this.demandesApprenantsAdapter = demandesApprenantsAdapter;
        this.demandesApprenantsFormatter = demandesApprenantsFormatter;
        this.storeService = storeService;
        this.contributionsInformationService = contributionsInformationService;
    }
    /**
     * Get a Demande Apprenant by reference
     *
     * @returns demande apprenant
     */
    async getDemandeApprenant(reference) {
        const payload = await this.demandesApprenantsAdapter.getDemandeApprenant(reference);
        if (isResultSuccess(payload)) {
            return payload.response;
        }
        throw payload.error ?? new Error('an unknown error occurred');
    }
    /**
     * Transforms filters to params for demandes-apprenants search
     * ? (i.e. transform a bourseVirtualStatus into a contribution-information search)
     *
     * @param filters - Front filters for demandes-apprenants
     * @returns params sent to search api
     */
    getSearchParamsFromFilters(filters) {
        const searchParams = { ...filters };
        if (searchParams.bourseVirtualStatus) {
            const contributionInformationIndex = searchParams.bourseVirtualStatus.findIndex((status) => this.contributionsInformationService.isTypeInformationId(status));
            if (contributionInformationIndex !== -1) {
                searchParams.contributionInformationType = searchParams.bourseVirtualStatus[contributionInformationIndex];
                searchParams.bourseVirtualStatus.splice(contributionInformationIndex, 1);
            }
        }
        return searchParams;
    }
    /**
     * Loads the list of demandes apprenant from the adapter service.
     *
     * @param pagination - An object containing the "from" and "size" properties for pagination.
     * @param filters - Optional filters to apply on the list of demandes.
     * @param demandesApprenantsStore - demandesApprenantsStore
     * @param totalFilteredDemandesApprenantsStore - totalFilteredDemandesApprenantsStore
     * @param aggregationsApprenantsStore - aggregationsApprenantsStore
     * @returns A promise that resolves when the list of demandes apprenant has been loaded.
     */
    async loadDemandesApprenants(pagination, filters, demandesApprenantsStore, totalFilteredDemandesApprenantsStore, aggregationsApprenantsStore) {
        if (!demandesApprenantsStore.loading()) {
            demandesApprenantsStore.SET_LOADING(true);
            try {
                const searchParams = this.getSearchParamsFromFilters(filters);
                const openedDemandesApprenants = await this.demandesApprenantsAdapter.searchDemandesApprenants(pagination, Object.keys(aggregationsApprenantsStore.data()).length === 0, searchParams);
                if (isResultSuccess(openedDemandesApprenants)) {
                    const formatted = await this.demandesApprenantsFormatter.formatDemandesApprenantsList(openedDemandesApprenants.response.data);
                    demandesApprenantsStore.SET_DATA(formatted);
                }
                else {
                    demandesApprenantsStore.SET_DATA([]);
                }
                totalFilteredDemandesApprenantsStore.SET_DATA((isResultSuccess(openedDemandesApprenants) && openedDemandesApprenants.response.meta.total) || 0);
                if (isResultSuccess(openedDemandesApprenants) &&
                    Object.keys(openedDemandesApprenants.response.aggregations).length > 0) {
                    aggregationsApprenantsStore.SET_DATA(openedDemandesApprenants.response.aggregations);
                }
            }
            catch (error) {
                demandesApprenantsStore.SET_ERROR(error);
            }
            demandesApprenantsStore.SET_LOADING(false);
        }
    }
    async loadOpenedDemandesApprenants(pagination, filters) {
        const demandesApprenantsStore = this.storeService.getOpenedDemandesApprenants();
        const totalFilteredDemandesApprenantsStore = this.storeService.getTotalFilteredOpenedDemandesApprenants();
        const aggregationsApprenantsStore = this.storeService.getOpenedAggregationsApprenants();
        filters.cloture = false;
        await this.loadDemandesApprenants(pagination, filters, demandesApprenantsStore, totalFilteredDemandesApprenantsStore, aggregationsApprenantsStore);
    }
    /**
     * Loads the list of closed demandes apprenants from the adapter service.
     */
    async loadClosedDemandesApprenants(pagination, filters) {
        const demandesApprenantsStore = this.storeService.getClosedDemandesApprenants();
        const totalFilteredDemandesApprenantsStore = this.storeService.getTotalFilteredClosedDemandesApprenants();
        const aggregationsApprenantsStore = this.storeService.getClosedAggregationsApprenants();
        filters.cloture = true;
        await this.loadDemandesApprenants(pagination, filters, demandesApprenantsStore, totalFilteredDemandesApprenantsStore, aggregationsApprenantsStore);
    }
    async loadTotalDemandesApprenants(filters, totalDemandesApprenantsStore) {
        // If the store module is not already loading data
        if (!totalDemandesApprenantsStore.loading()) {
            // Set the store module to loading mode
            totalDemandesApprenantsStore.SET_LOADING(true);
            try {
                // Call the adapter service and wait for the response
                const payload = await this.demandesApprenantsAdapter.countDemandesApprenants(filters);
                // Set the response data on the store module
                totalDemandesApprenantsStore.SET_DATA((isResultSuccess(payload) && payload.response) || 0);
            }
            catch (error) {
                // Set the error on the store module
                totalDemandesApprenantsStore.SET_ERROR(error);
            }
            // Set the store module to not loading mode
            totalDemandesApprenantsStore.SET_LOADING(false);
        }
    }
    /**
     * Loads the count of closed demandes financement from the adapter service.
     */
    async loadTotalOpenedDemandesApprenants(filters = {}) {
        const totalDemandesApprenantsStore = this.storeService.getTotalOpenedDemandesApprenants();
        filters.cloture = false;
        await this.loadTotalDemandesApprenants(filters, totalDemandesApprenantsStore);
    }
    /**
     * Loads the count of closed demandes financement from the adapter service.
     */
    async loadTotalClosedDemandesApprenants(filters = {}) {
        const totalDemandesApprenantsStore = this.storeService.getTotalClosedDemandesApprenants();
        filters.cloture = true;
        await this.loadTotalDemandesApprenants(filters, totalDemandesApprenantsStore);
    }
    /**
     * Get the demandes apprenants with contribution-vef href
     */
    getDemandesApprenantsWithContributionVEFHref(pagination, additionalFilters) {
        return this.demandesApprenantsAdapter.searchDemandesApprenants(pagination, false, {
            contributionVEFStatus: ['REQUESTED', 'CHECKED'],
            formationDateStarted: true,
            cloture: false,
            ...additionalFilters,
        }, ['bourse.contributionVEFHref'], false);
    }
}
export { DemandesApprenantsService };
//# sourceMappingURL=demandes-apprenants-service.js.map